import { useCallback, useMemo, useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { HOMEPAGE_PRODUCT_LINEUP_TEMPLATE } from '../constants/homepageProducts';
import { suggestRecoveryProduct } from '../lib/suggestRecoveryProduct';

const INTRO =
  "Hi — I'm the SprintFit recovery assistant. Describe what's going on (e.g. “tight shoulders from desk work” or “knees after today's long run”) and I'll point you to the best match on our shelf.";

function buildLineupMap(lineup) {
  return lineup.reduce((acc, p) => {
    acc[p.id] = p;
    return acc;
  }, {});
}

export function RecoveryChatbot() {
  const lineupById = useMemo(() => buildLineupMap(HOMEPAGE_PRODUCT_LINEUP_TEMPLATE), []);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState(() => [
    { role: 'assistant', text: INTRO },
  ]);

  const send = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
    const { product, reason } = suggestRecoveryProduct(trimmed, lineupById);
    const name = product?.name ?? 'ThermaCube';
    const shopUrl = product?.shopUrl ?? '#product-rail';
    const reply =
      `Based on what you shared, **${name}** is the best match.\n\n${reason}\n\nI can walk you through how athletes use it in our Science section, or you can go straight to the storefront.`;
    setMessages((prev) => [...prev, { role: 'assistant', text: reply, shopUrl, productName: name }]);
  }, [input, lineupById]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 right-5 z-[100] flex h-14 w-14 items-center justify-center rounded-full bg-[#1c1b19] text-[#f5f3ef] shadow-[0_12px_40px_-8px_rgba(0,0,0,0.45)] ring-2 ring-[#2c5f54]/30 hover:bg-[#2c5f54] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400"
        aria-expanded={open}
        aria-label={open ? 'Close recovery assistant' : 'Open recovery assistant'}
      >
        {open ? <X className="w-6 h-6" aria-hidden /> : <MessageCircle className="w-7 h-7" aria-hidden />}
      </button>

      {open ? (
        <div
          className="fixed bottom-24 right-5 z-[100] flex w-[min(100vw-2.5rem,22rem)] max-h-[min(70vh,28rem)] flex-col overflow-hidden rounded-2xl border border-stone-200/90 bg-[#faf8f5] shadow-[0_24px_60px_-20px_rgba(28,27,25,0.35)]"
          role="dialog"
          aria-label="Recovery assistant"
        >
          <div className="border-b border-stone-200/80 bg-[#1a2623] px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#c9a962]/90">SprintFit</p>
            <p className="font-heading text-sm font-medium text-white">Recovery assistant</p>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 text-sm">
            {messages.map((m, i) => (
              <div
                key={`${i}-${m.role}`}
                className={`rounded-xl px-3 py-2.5 ${
                  m.role === 'user'
                    ? 'ml-6 bg-[#2c5f54]/12 text-stone-900 border border-[#2c5f54]/15'
                    : 'mr-4 bg-white text-stone-700 border border-stone-200/90 shadow-sm'
                }`}
              >
                <p className="whitespace-pre-wrap leading-relaxed [&_strong]:font-semibold [&_strong]:text-stone-900">
                  {m.text.split('**').map((chunk, j) =>
                    j % 2 === 1 ? (
                      <strong key={j}>{chunk}</strong>
                    ) : (
                      <span key={j}>{chunk}</span>
                    )
                  )}
                </p>
                {m.shopUrl ? (
                  <a
                    href={m.shopUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex w-full items-center justify-center rounded-lg bg-[#1c1b19] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#f5f3ef] hover:bg-[#2c5f54] transition-colors"
                  >
                    Shop {m.productName}
                  </a>
                ) : null}
              </div>
            ))}
          </div>
          <div className="border-t border-stone-200/80 p-3 flex gap-2 bg-white/80">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Knees hurt after my run…"
              className="min-w-0 flex-1 rounded-lg border border-stone-300/90 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2c5f54]/40"
              aria-label="Your recovery question"
            />
            <button
              type="button"
              onClick={send}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#1c1b19] text-white hover:bg-[#2c5f54] transition-colors"
              aria-label="Send"
            >
              <Send className="w-4 h-4" aria-hidden />
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
